/**
 * ADMIN-T016 / ADMIN-T017 (D12-B) — session helpers.
 *
 * State lives in PermissionStore (Pinia). `currentSession` is a computed ref
 * derived from the store — reactive in templates without a separate ref.
 *
 * Short-term local/admin UX: the login envelope is mirrored to sessionStorage so
 * a same-tab refresh can restore the store and then revalidate via /me/permissions.
 * This is not a production auth design and never stores the password.
 *
 * Requires setActivePinia() to be called in main.ts before bootstrap so the
 * store is accessible during the PermissionPriming phase.
 */
import { computed } from 'vue';
import { httpClient } from '@/data/api/client';
import { usePermissionStore } from '@/permission/store';
import type { LoginEnvelope, MeEnvelope } from '@/permission/store';

export type { LoginEnvelope, MeEnvelope };

export const SESSION_STORAGE_KEY = 'liju.admin.session.v1';

interface PersistedSession {
  version: 1;
  login: LoginEnvelope;
}

function sessionStorageSafe(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isLoginEnvelope(value: unknown): value is LoginEnvelope {
  if (value === null || typeof value !== 'object') return false;
  const env = value as Partial<LoginEnvelope>;
  if (
    typeof env.token !== 'string' ||
    typeof env.userId !== 'string' ||
    typeof env.userName !== 'string' ||
    !isStringArray(env.permissions) ||
    !isStringArray(env.regionScope) ||
    !isStringArray(env.projectScope)
  ) {
    return false;
  }
  if (env.permissionVersion !== undefined && typeof env.permissionVersion !== 'string') return false;
  if (env.user !== undefined) {
    if (env.user === null || typeof env.user !== 'object') return false;
    const user = env.user as NonNullable<LoginEnvelope['user']>;
    if (user.actorId !== undefined && typeof user.actorId !== 'string') return false;
    if (user.displayName !== undefined && user.displayName !== null && typeof user.displayName !== 'string') return false;
    if (user.role !== undefined && typeof user.role !== 'string') return false;
  }
  return true;
}

function isPersistedSession(value: unknown): value is PersistedSession {
  if (value === null || typeof value !== 'object') return false;
  const persisted = value as Partial<PersistedSession>;
  return persisted.version === 1 && isLoginEnvelope(persisted.login);
}

function persistSession(env: LoginEnvelope): void {
  const storage = sessionStorageSafe();
  if (!storage) return;
  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ version: 1, login: env } satisfies PersistedSession));
  } catch {
    // Best-effort UX cache only; login must still succeed if storage is blocked.
  }
}

export function clearPersistedSession(): void {
  const storage = sessionStorageSafe();
  if (!storage) return;
  try {
    storage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Best-effort cleanup only.
  }
}

export function restoreSessionFromStorage(): boolean {
  const storage = sessionStorageSafe();
  if (!storage) return false;
  try {
    const raw = storage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return false;
    const parsed: unknown = JSON.parse(raw);
    if (!isPersistedSession(parsed)) {
      storage.removeItem(SESSION_STORAGE_KEY);
      return false;
    }
    usePermissionStore().setFromLogin(parsed.login);
    return true;
  } catch {
    try { storage.removeItem(SESSION_STORAGE_KEY); } catch { /* noop */ }
    return false;
  }
}

export function expireSession(): void {
  clearPersistedSession();
  try { usePermissionStore().clear(); } catch { /* Pinia may not be active in early tests. */ }
}

/** Login: POST /admin-bff/auth/login. Populates PermissionStore on success. */
export async function login(username: string, password: string): Promise<LoginEnvelope> {
  const res = await httpClient.request<LoginEnvelope>({
    method: 'POST',
    url: '/auth/login',
    body: { username, password },
  });
  usePermissionStore().setFromLogin(res);
  persistSession(res);
  return res;
}

/**
 * Refresh permissions from the server. Called during PermissionPriming and on
 * re-authentication. Requires a valid Bearer token already in the store.
 */
export async function fetchPermissions(): Promise<MeEnvelope> {
  const res = await httpClient.get<MeEnvelope>('/me/permissions');
  usePermissionStore().setFromMe(res);
  return res;
}

/**
 * Clear the session — redirects to login handled by caller.
 *
 * Best-effort notifies the server (POST /admin-bff/auth/logout, stateless 204) so
 * the token is invalidated server-side too (ADMIN-T357 / CORE-T258), then wipes the
 * in-memory envelope and its same-tab sessionStorage mirror. The server call is
 * fire-and-forget and never blocks logout.
 */
export function logout(): void {
  void httpClient.request({ method: 'POST', url: '/auth/logout' }).catch(() => undefined);
  expireSession();
}

/**
 * Reactive session handle. `.value` is non-null when authenticated.
 * Tracked against PermissionStore so templates re-render on login/logout.
 */
export const currentSession = computed(() => {
  try {
    const store = usePermissionStore();
    if (!store.isAuthenticated) return null;
    return {
      userId: store.userId!,
      userName: store.userName!,
      token: store.token!,
      permissions: store.permissions,
      regionScope: store.regionScope,
      projectScope: store.projectScope,
    };
  } catch {
    // Pinia not yet active (called before setActivePinia in main.ts).
    return null;
  }
});

/** Non-reactive point-in-time auth check. */
export function isAuthenticated(): boolean {
  try { return usePermissionStore().isAuthenticated; }
  catch { return false; }
}

/** @deprecated Use `login()`. Kept for backward compat with M1 tests. */
export const fakeLogin = login;

/** @deprecated Use `logout()`. */
export function clearSession(): void { logout(); }
